<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

const props = defineProps({
    align: {
        type: String,
        default: 'right',
    },
    width: {
        type: String,
        default: '48',
    },
    contentClasses: {
        type: Array,
        default: () => ['py-1', 'bg-white dark:bg-gray-700'],
    },
});

let open = ref(false);
const dropdownContainer = ref(null);
const dropdownStyle = ref({});

const closeOnEscape = (e) => {
    if (open.value && e.key === 'Escape') {
        open.value = false;
    }
};

const calculatePosition = () => {
    if (!dropdownContainer.value) return;
    
    const rect = dropdownContainer.value.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    // Default position (below trigger)
    let top = rect.bottom + 5;
    
    // If not enough space below, and more space above, flip to above
    if (spaceBelow < 200 && spaceAbove > spaceBelow) {
        top = rect.top - 5;
        dropdownStyle.value = {
            bottom: `${window.innerHeight - top}px`,
            [props.align === 'right' ? 'right' : 'left']: `${props.align === 'right' ? window.innerWidth - rect.right : rect.left}px`,
        };
    } else {
        dropdownStyle.value = {
            top: `${top}px`,
            [props.align === 'right' ? 'right' : 'left']: `${props.align === 'right' ? window.innerWidth - rect.right : rect.left}px`,
        };
    }
};

const toggleDropdown = () => {
    open.value = !open.value;
    if (open.value) {
        setTimeout(() => {
            calculatePosition();
        }, 0);
    }
};

onMounted(() => {
    document.addEventListener('keydown', closeOnEscape);
    window.addEventListener('resize', calculatePosition);
    window.addEventListener('scroll', calculatePosition);
});

onUnmounted(() => {
    document.removeEventListener('keydown', closeOnEscape);
    window.removeEventListener('resize', calculatePosition);
    window.removeEventListener('scroll', calculatePosition);
});

const widthClass = computed(() => {
    return {
        '48': 'w-48',
    }[props.width.toString()];
});

const alignmentClasses = computed(() => {
    if (props.align === 'left') {
        return 'origin-top-left';
    }

    if (props.align === 'right') {
        return 'origin-top-right';
    }

    return 'origin-top';
});
</script>

<template>
    <div class="relative" ref="dropdownContainer">
        <div @click="toggleDropdown">
            <slot name="trigger" />
        </div>

        <!-- Full Screen Dropdown Overlay -->
        <div v-show="open" class="fixed inset-0 z-40" @click="open = false" />

        <Teleport to="body">
            <transition
                enter-active-class="transition ease-out duration-200"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95"
            >
                <div
                    v-if="open"
                    class="fixed z-[9999] rounded-md shadow-lg"
                    :class="[widthClass]"
                    :style="dropdownStyle"
                    @click="open = false"
                >
                    <div class="rounded-md ring-1 ring-black ring-opacity-5" :class="contentClasses">
                        <slot name="content" />
                    </div>
                </div>
            </transition>
        </Teleport>
    </div>
</template>
